import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  useTheme,
  Chip,
  keyframes,
  styled
} from '@mui/material';
import { Link } from 'react-router-dom';
import { PlayArrowRounded, TrendingUpRounded } from '@mui/icons-material';

// Keyframe animations
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  25% { transform: translateY(-20px) rotate(5deg); }
  50% { transform: translateY(-15px) rotate(-3deg); }
  75% { transform: translateY(-25px) rotate(7deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gradient = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const particleFloat = keyframes`
  0% { transform: translateY(0px) translateX(0px) rotate(0deg); }
  33% { transform: translateY(-30px) translateX(15px) rotate(120deg); }
  66% { transform: translateY(-10px) translateX(-10px) rotate(240deg); }
  100% { transform: translateY(0px) translateX(0px) rotate(360deg); }
`;

// Styled components
const GlassContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  background: `linear-gradient(135deg,
    ${theme.palette.background.paper}30 0%,
    ${theme.palette.background.paper}28 15%,
    ${theme.palette.background.paper}25 30%,
    ${theme.palette.primary.main}18 45%,
    ${theme.palette.primary.main}12 60%,
    ${theme.palette.primary.main}08 75%,
    ${theme.palette.secondary.main}06 90%,
    ${theme.palette.secondary.main}04 100%)`,
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: 'none',
  borderRadius: '32px',
  padding: '60px 40px',
  margin: '20px',
  boxShadow: `
    0 0 80px 40px ${theme.palette.primary.main}08,
    0 0 120px 60px ${theme.palette.primary.main}05,
    0 0 160px 80px ${theme.palette.primary.main}03,
    0 8px 32px ${theme.palette.primary.main}15,
    inset 0 0 60px ${theme.palette.primary.main}08
  `,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 30% 40%, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}08 30%, transparent 50%),
      radial-gradient(circle at 70% 60%, ${theme.palette.secondary.main}10 0%, ${theme.palette.secondary.main}05 30%, transparent 50%)
    `,
    borderRadius: 'inherit',
    zIndex: -1,
  }
}));

const ParticleBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  zIndex: 1,
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    borderRadius: '50%',
    background: `linear-gradient(45deg, ${theme.palette.primary.main}60, ${theme.palette.secondary.main}40)`,
    animation: `${particleFloat} 8s ease-in-out infinite`,
    filter: 'blur(2px)',
  },
  '&::before': {
    width: '120px',
    height: '120px',
    top: '15%',
    left: '10%',
    animationDelay: '0s',
    animationDuration: '12s',
  },
  '&::after': {
    width: '80px',
    height: '80px',
    top: '70%',
    right: '15%',
    animationDelay: '6s',
    animationDuration: '10s',
  }
}));

const GeometricShapes = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  zIndex: 1,
  '& .shape': {
    position: 'absolute',
    border: `2px solid ${theme.palette.primary.main}40`,
    borderRadius: '12px',
    animation: `${float} 6s ease-in-out infinite`,
  },
  '& .shape-1': {
    width: '60px',
    height: '60px',
    top: '25%',
    right: '20%',
    transform: 'rotate(45deg)',
    animationDelay: '0s',
  },
  '& .shape-2': {
    width: '40px',
    height: '40px',
    bottom: '30%',
    left: '15%',
    borderRadius: '50%',
    animationDelay: '2s',
  },
  '& .shape-3': {
    width: '80px',
    height: '40px',
    top: '60%',
    right: '30%',
    animationDelay: '4s',
  }
}));

const AnimatedTitle = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.info.main})`,
  backgroundSize: '200% 200%',
  animation: `${gradient} 4s ease infinite`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontSize: 'clamp(2.5rem, 8vw, 4.5rem)',
  fontWeight: 800,
  textAlign: 'center',
  marginBottom: '24px',
  letterSpacing: '-0.02em',
  textShadow: '0 0 30px rgba(0, 188, 212, 0.3)',
}));

const GlowButton = styled(Button)(({ theme }) => ({
  padding: '16px 32px',
  fontSize: '1.1rem',
  fontWeight: 600,
  borderRadius: '12px',
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  boxShadow: `0 8px 32px ${theme.palette.primary.main}40`,
  transition: 'all 0.3s ease-in-out',
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
    transition: 'left 0.6s',
  },

  '&:hover': {
    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
    transform: 'translateY(-3px) scale(1.02)',
    boxShadow: `0 12px 48px ${theme.palette.primary.main}60`,

    '&::before': {
      left: '100%',
    }
  },

  '&:active': {
    transform: 'translateY(-1px) scale(0.98)',
  }
}));

interface ModernHeroSectionProps {
  title?: string;
  subtitle?: string;
  description?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  primaryButtonLink?: string;
  stats?: Array<{
    number: string;
    label: string;
  }>;
}

const ModernHeroSection: React.FC<ModernHeroSectionProps> = ({
  title = "Smart Storage Checker",
  subtitle = "Die Zukunft der Energiespeicherung beginnt hier",
  description = "Systematische Analyse für Hybrid-Speichersysteme. Identifizieren Sie Potentiale für mehr Effizienz und Kosteneinsparungen.",
  primaryButtonText = "Jetzt Analyse starten",
  secondaryButtonText = "Demo ansehen",
  primaryButtonLink = "/upload",
  stats = [
    { number: '1-2 Minuten', label: 'Geschätzte Zeit' },
    { number: 'Beta', label: 'Version' },
    { number: '100%', label: 'Kostenlos' }
  ]
}) => {
  const theme = useTheme();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        transition: 'background 0.3s ease',
      }}
    >
      {/* Animated Background Layers */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 20%, ${theme.palette.primary.main}12 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, ${theme.palette.secondary.main}10 0%, transparent 50%),
            radial-gradient(circle at 60% 40%, ${theme.palette.info.main}08 0%, transparent 50%)
          `,
          animation: `${pulse} 8s ease-in-out infinite`,
          zIndex: 0,
        }}
      />

      {/* Floating Particles */}
      <ParticleBox />

      {/* Geometric Shapes */}
      <GeometricShapes>
        <div className="shape shape-1" />
        <div className="shape shape-2" />
        <div className="shape shape-3" />
      </GeometricShapes>

      <Container maxWidth={false} sx={{ position: 'relative', zIndex: 3, py: 8, px: { xs: 2, sm: 4, md: 6 }, maxWidth: '1400px', mx: 'auto', overflow: 'visible' }}>
        {/* Glow Effect Wrapper */}
        <Box
          sx={{
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-80px',
              left: '-80px',
              right: '-80px',
              bottom: '-80px',
              background: `radial-gradient(ellipse at center, ${theme.palette.primary.main}25 0%, ${theme.palette.primary.main}15 30%, ${theme.palette.primary.main}08 50%, transparent 70%)`,
              borderRadius: '50%',
              filter: 'blur(60px)',
              zIndex: -1,
              pointerEvents: 'none',
            }
          }}
        >
          <GlassContainer>
          {/* Innovation Badge */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Chip
              label="🔧 Energiespeicher-Analyse Tool"
              color="primary"
              variant="outlined"
              sx={{
                fontSize: '1rem',
                py: 1,
                px: 3,
                backdropFilter: 'blur(10px)',
                background: `${theme.palette.primary.main}15`,
                border: `2px solid ${theme.palette.primary.main}50`,
              }}
            />
          </Box>

          {/* Animated Title */}
          <AnimatedTitle variant="h1">
            {title}
          </AnimatedTitle>

          {/* Subtitle */}
          <Typography
            variant="h3"
            color="text.primary"
            sx={{
              mb: 4,
              fontWeight: 400,
              maxWidth: 1000,
              mx: 'auto',
              textAlign: 'center',
              fontSize: { xs: '1.5rem', md: '2.2rem' },
              opacity: 0.9,
              animation: `${slideInUp} 1s ease-out 0.3s both`
            }}
          >
            {subtitle}
          </Typography>

          {/* Description */}
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              maxWidth: 900,
              mx: 'auto',
              mb: 6,
              textAlign: 'center',
              lineHeight: 1.7,
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              fontWeight: 300,
              animation: `${slideInUp} 1s ease-out 0.6s both`
            }}
          >
            {description}
          </Typography>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 3,
              justifyContent: 'center',
              flexWrap: 'wrap',
              mb: 6,
              animation: `${slideInUp} 1s ease-out 0.9s both`
            }}
          >
            <Button
              component={Link}
              to={primaryButtonLink}
              variant="contained"
              size="large"
              startIcon={<PlayArrowRounded />}
              sx={{
                padding: '16px 32px',
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: '12px',
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                boxShadow: `0 8px 32px ${theme.palette.primary.main}40`,
                textTransform: 'none',
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                  transform: 'translateY(-3px) scale(1.02)',
                  boxShadow: `0 12px 48px ${theme.palette.primary.main}60`,
                }
              }}
            >
              {primaryButtonText}
            </Button>

            {/* Secondary button commented out per request
            <Button
              variant="outlined"
              size="large"
              sx={{
                py: 2,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 3,
                borderWidth: 2,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                backdropFilter: 'blur(10px)',
                background: `${theme.palette.primary.main}10`,
                '&:hover': {
                  borderWidth: 2,
                  background: `${theme.palette.primary.main}20`,
                  transform: 'translateY(-3px) scale(1.02)',
                  boxShadow: `0 8px 32px ${theme.palette.primary.main}30`,
                }
              }}
            >
              {secondaryButtonText}
            </Button>
            */}
          </Box>

          {/* Stats */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 4,
              flexWrap: 'wrap',
              animation: `${slideInUp} 1s ease-out 1.2s both`
            }}
          >
            {stats.map((stat, idx) => (
              <Box
                key={idx}
                sx={{
                  textAlign: 'center',
                  p: 3,
                  borderRadius: 3,
                  background: `${theme.palette.primary.main}10`,
                  border: `1px solid ${theme.palette.primary.main}20`,
                  backdropFilter: 'blur(10px)',
                  minWidth: '120px',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    background: `${theme.palette.primary.main}15`,
                    transition: 'all 0.3s ease-in-out',
                    boxShadow: `0 8px 24px ${theme.palette.primary.main}25`,
                  }
                }}
              >
                <Typography
                  variant="h3"
                  color="primary.main"
                  sx={{ fontWeight: 800, mb: 0.5 }}
                >
                  {stat.number}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ fontWeight: 500 }}
                >
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </GlassContainer>
      </Box>
      </Container>
    </Box>
  );
};

export default ModernHeroSection;